import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import PropertyGrid from "@/components/sections/PropertyGrid";
import heroImage from "@/assets/dr-investing-hero.jpg";
import drListings from "@/data/drListings";
import { useProperties } from "@/hooks/useProperties";

const DRListings = () => {
  useEffect(() => {
    document.title = "Dominican Republic Properties | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Browse available properties in the Dominican Republic. Hadassah Homes offers expert guidance on DR real estate investments, vacation homes, and luxury residences.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/dominican-republic-investing/properties";
  }, []);

  const { data: cmsProps } = useProperties({ region: "DR", withImages: false });

  // Build the list: start with hardcoded fallback, then for each Supabase published property,
  // either replace the fallback entry by slug or prepend the new one.
  const fallbackListings = drListings.map((l) => ({
    image: l.images[0],
    status: l.tag,
    price: l.price,
    priceNote: l.priceNote,
    address: `${l.name} · ${l.location}`,
    beds: undefined,
    baths: undefined,
    sqft: l.sqft,
    href: `/dominican-republic-investing/properties/${l.slug}`,
    _slug: l.slug,
  }));

  const merged = [...fallbackListings];
  cmsProps.forEach((p) => {
    const cmsItem = {
      image: p.hero_image_url || fallbackListings.find((f) => f._slug === p.slug)?.image || "",
      status: p.listing_status ?? "",
      price: p.price ?? "",
      priceNote: undefined as string | undefined,
      address: p.location ? `${p.title} · ${p.location}` : p.title,
      beds: p.bedrooms ?? undefined,
      baths: p.bathrooms ?? undefined,
      sqft: p.square_feet ? String(p.square_feet) : "",
      href: `/dominican-republic-investing/properties/${p.slug}`,
      _slug: p.slug,
    };
    const idx = merged.findIndex((m) => m._slug === p.slug);
    if (idx >= 0) merged[idx] = cmsItem;
    else merged.unshift(cmsItem);
  });

  const listings = merged.map(({ _slug, ...rest }) => rest);

  return (
    <SiteLayout>
      <PageHero
        compact
        title="Dominican Republic Properties"
        subtitle="Beachfront residences, luxury towers, and investment opportunities"
        backgroundImage={heroImage}
      />

      <ContentSection>
        <div className="flex items-center justify-between mb-10">
          <p className="text-muted-foreground text-sm font-body uppercase tracking-widest">
            {listings.length} Properties
          </p>
          <Link
            to="/dominican-republic"
            className="font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
          >
            Learn About DR Investing →
          </Link>
        </div>
        <PropertyGrid listings={listings} columns={3} />
      </ContentSection>

      <ContentSection variant="dark">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <p className="font-body text-[11px] uppercase tracking-[0.3em] text-accent">Looking for Something Specific?</p>
          <h2 className="font-headline text-3xl md:text-4xl text-secondary-foreground leading-tight">
            Not Every Opportunity Is Listed Here
          </h2>
          <p className="text-secondary-foreground/80 text-base md:text-lg leading-relaxed">
            We work with additional developers and off-market opportunities across the Dominican Republic. Book a consultation and we'll match you with properties that fit your goals and budget.
          </p>
          <Link
            to="/consultation"
            className="inline-block font-body text-sm uppercase tracking-widest text-accent hover:text-accent/80 transition-colors"
          >
            Book a Consultation →
          </Link>
        </div>
      </ContentSection>

    </SiteLayout>
  );
};

export default DRListings;
